import React from 'react';
import PropTypes from 'prop-types';
import { Button, Tooltip } from '@getro/rombo';
import { Box, Flex, Text } from 'rebass/styled-components';
import LayoutSecondaryActionsSchema from '../../../schemas/layoutSecondaryActions';

const renderSecondaryAction = (
  { action, icon, text, popoverContent, popoverContentSx = {}, sx = {}, loading = false },
  index,
) => {
  const button = (
    <Button
      size="small"
      variant="tertiary"
      onClick={action}
      key={text}
      sx={{
        pr: '0',
        pt: '2px',
        ml: 'auto',
        justifyContent: 'center',
        ...sx,
      }}
      loading={loading}
    >
      {icon && (
        <Box
          as={icon}
          strokeWidth="1.5"
          aria-hidden="true"
          mr="1"
          width="1em"
          height="1em"
          sx={{ verticalAlign: 'middle' }}
        />
      )}
      {text}
    </Button>
  );

  if (popoverContent) {
    return (
      <Tooltip
        sx={{ minWidth: 'max-content !important', ...popoverContentSx }}
        trigger={action ? button : <Text key={text}>{text}</Text>}
        content={popoverContent}
        key={index}
        data-cy="page-layout-popover"
        position="top-end"
      />
    );
  }

  return button;
};

const PageLayoutContent = ({
  buttonOnClick,
  buttonText,
  children,
  fullWidth,
  header,
  preHeader,
  secondaryActions,
  secondaryActionProps,
  contentHeader,
  contentProps,
  innerContentProps,
}) => {
  const showButton = buttonOnClick && buttonText;
  return (
    <Flex flexDirection="column" flex="1" px="16px" {...contentProps}>
      {contentHeader}
      <Box
        sx={!fullWidth ? { maxWidth: 720, width: '100%', margin: '0px auto' } : { width: '100%' }}
        className={`PageLayoutContent ${fullWidth && 'PageLayoutContent--full-width'}`}
        data-cy="page-layout"
      >
        <Box className="PageLayoutContent__inner" mx="auto" {...innerContentProps}>
          {preHeader && <div className="PageLayoutContent__pre-header">{preHeader}</div>}

          {(header || showButton || !!secondaryActions.length) && (
            <Flex pb={3} pt="2px" fontSize={1} data-cy="page-layout-header">
              {header}
              {showButton && (
                <Button
                  size="small"
                  variant="secondary"
                  ml={3}
                  display={['none', 'none', 'flex']}
                  alignItems="center"
                  onClick={buttonOnClick}
                >
                  {buttonText}
                </Button>
              )}
              {!!secondaryActions.length && (
                <Box sx={{ marginLeft: 'auto' }} {...secondaryActionProps}>
                  {secondaryActions.map(renderSecondaryAction)}
                </Box>
              )}
            </Flex>
          )}

          {children}
        </Box>
      </Box>
    </Flex>
  );
};

PageLayoutContent.propTypes = {
  buttonOnClick: PropTypes.func,
  buttonText: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  children: PropTypes.node,
  fullWidth: PropTypes.bool,
  header: PropTypes.node,
  preHeader: PropTypes.node,
  secondaryActions: LayoutSecondaryActionsSchema,
  contentHeader: PropTypes.node,
  secondaryActionProps: PropTypes.object,
  contentProps: PropTypes.object,
  innerContentProps: PropTypes.object,
};

PageLayoutContent.defaultProps = {
  buttonOnClick: null,
  buttonText: null,
  fullWidth: false,
  header: null,
  preHeader: null,
  secondaryActions: [],
  children: null,
  contentHeader: null,
  secondaryActionProps: {},
  contentProps: {},
  innerContentProps: {},
};

export default PageLayoutContent;
